<template>
  <div class="body-container body_container--section_nav page-links no-images show-all-items visual_menu--glass_effect textual_menu--glass_effect"
       :class="containerClasses">
    <div class="viewport" ref="scroller">
      <div class="body-container-scroller">
        <menu-section-list-item v-for="section in sections || []" :key="'nav--' + section.ms_id"
                                :section="section" :is-highlighted="navButtonHighlighted === section.ms_id"
                                @click.prevent.stop.native="navButtonClickHandler"></menu-section-list-item>
      </div>
      <a class="page-link-view-more btn" href="#">VIEW MORE</a>
    </div>
  </div>
</template>

<script lang="ts">
/**
 * Section nav widget for menus.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.6.0
 * @copyright Copyright (C) 2022-2024, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *  2022-01-07 (1.0.0) (jhoward)
 *    - Refactored from textual-menu.vue to be used with visual-menu.vue as well.
 *
 *  2022-01-11 (1.1.0) (jhoward)
 *    - Fix section nav buttons being scrolled into view when highlighted.
 *    - Fix logic for auto-highlighting section nav buttons while scrolling the page.
 *
 *  2022-04-05 (1.1.1) (jhoward)
 *    - Fix undefined reference in <menu-sections />::navButtonHighlighted().
 *
 *  2022-05-24 (1.2.0) (jhoward)
 *    - <menu-sections> now uses .visual_menu--glass_effect class for stylization.
 *
 *  2022-10-04 (1.3.0) (jhoward)
 *    - <menu-sections> now only includes sections that are set to show in the navigation.
 *
 *  2023-04-24 (1.4.0) (jhoward)
 *    - Add TypeScript support to <menu-sections>.
 *
 *  2024-07-09 (1.4.1) (jhoward)
 *    - Add accessibility attributes in <menu-sections>.
 *    - Make accessibility-related style changes in <menu-sections>.
 *
 *  2024-07-10 (1.5.0) (jhoward)
 *    - Remove "selected menu item" button from <menu-sections>.
 *
 *  2024-08-19 (1.6.0) (jhoward)
 *    - Rename <menu-sections> to <menu-section-list>.
 *    - Refactor <menu-section-list> to use the new <menu-section-list-item> component.
 */

import {defineComponent, PropType} from "vue";
import {MenuSection, VisualMenuComponent} from "./defs";
import MenuSectionListItem from "./menu-section-list-item.vue";

export default defineComponent({
  name: "menu-section-list",
  components: {MenuSectionListItem},
  props: {
    sections: {
      type: Array as PropType<MenuSection[]>,
      default: () => []
    },
    currentSection: {
      type: Number,
      default: -1
    }
  },
  data: function () {
    return {
      containerObserver: null as IntersectionObserver | null,
      sectionObserver: null as IntersectionObserver | null,
      isStickyStuck: false,
      isFixedPosition: false,
      navButtonBuffer: 16, //in pixels

      navButtonHighlighted: this.currentSection,
      navButtonHighlightTimeout: 500, //in milliseconds, time it takes for window.scrollTo to smooth scroll
      navButtonHighlightTimer: -1,
    };
  },
  mounted: function () {
    this.isFixedPosition = (window.getComputedStyle(this.$el).position === 'fixed');

    this.setupIntersectionObservers();
  },
  computed: {
    containerClasses: function () {
      return {
        'sticky-stuck': this.isStickyStuck || this.isFixedPosition
      };
    }
  },
  watch: {
    sections: function () {
      this.setupIntersectionObservers();
    },
    navButtonHighlighted: function () {
      const currentSectionButton = this.$refs['menu_nav--btn_' + this.currentSection] as HTMLAnchorElement[];
      if (currentSectionButton && currentSectionButton.length) {
        const boundingClientRect = currentSectionButton[0].getBoundingClientRect();
        if (this.$refs?.scroller && boundingClientRect.left < this.navButtonBuffer) {
          (this.$refs.scroller as HTMLDivElement).scrollTo({
            behavior: 'smooth',
            left: (this.$refs.scroller as HTMLDivElement).scrollLeft + boundingClientRect.left - this.navButtonBuffer
          });
        } else if (this.$refs?.scroller && boundingClientRect.right + this.navButtonBuffer > (this.$refs.scroller as HTMLDivElement).clientWidth) {
          (this.$refs.scroller as HTMLDivElement).scrollTo({
            behavior: 'smooth',
            left: (this.$refs.scroller as HTMLDivElement).scrollLeft + (boundingClientRect.right + this.navButtonBuffer - (this.$refs.scroller as HTMLDivElement).clientWidth)
          });
        }
      }
    },
    currentSection: function () {
      this.setupNavButtonTimer();
    }
  },
  methods: {

    setupNavButtonTimer: function () {
      clearTimeout(this.navButtonHighlightTimer);
      this.navButtonHighlightTimer = window.setTimeout(() => {
        this.navButtonHighlighted = this.currentSection;
      }, this.navButtonHighlightTimeout);
    },

    checkStickyStuck: function (entries: IntersectionObserverEntry[]) {
      this.isStickyStuck = (entries[0].intersectionRatio < 1);
    },

    observeCurrentSection: function (entries: IntersectionObserverEntry[]) {
      if (1 === entries.length && entries[0].intersectionRatio !== 0) {
        this.$emit('update:currentSection', parseInt((entries[0].target as HTMLElement).dataset?.sectionId || '0', 10));
      }
    },

    // calculateRootMarginBottom: function () {
    //   return -1 * this.$el.clientHeight;
    // },
    //
    // calculateRootMarginTop: function () {
    //   const menuContainerNode = this.$el.closest('.visual_menu--container, .textual_menu--container');
    //   if (menuContainerNode) {
    //     const searchContainerNode = menuContainerNode.querySelector('.search--container');
    //     if (searchContainerNode) {
    //       return -1 * searchContainerNode.clientHeight;
    //     }
    //   }
    //   return 0;
    // },

    setupIntersectionObservers: function () {
      if (window.hasOwnProperty('IntersectionObserver')) {
        //only need to set up the observer once
        if (!this.sectionObserver) {
          this.sectionObserver = new IntersectionObserver(this.observeCurrentSection, {
            root: null,	//defaults to viewport
            rootMargin: '-400px 0px -300px 0px',//rootMarginTop + 'px 0px 0px 0px',
            threshold: [0, 1]
          });
        }

        if (this.$parent) {
          this.$parent.$el.querySelectorAll('.textual_menu--section, .visual_menu--section').forEach(el => {
            this.sectionObserver?.observe(el);
          });
        }

        if (!this.containerObserver && !this.isFixedPosition) {
          this.containerObserver = new IntersectionObserver(this.checkStickyStuck, {
            threshold: [1]
          });
          this.containerObserver.observe(this.$el);
        }
      }
    },

    navButtonClickHandler: function (event: Event) {
      const href = (event.target as HTMLAnchorElement | null)?.getAttribute('href')?.replace('#', '') || '';
      const parentComponent: VisualMenuComponent | null = this.$parent as VisualMenuComponent | null;
      if (parentComponent) {
        for (let i = 0, l = parentComponent.sectionOffsets.length; i < l; i++) {
          if (parentComponent.sectionOffsets[i].hash === href) {
            parentComponent.scrollToSection(parentComponent.sectionOffsets[i]);
            break;
          }
        }
      }
    },
  }
})
</script>

<style scoped>
.body_container--section_nav {
  touch-action: pan-x;
}

.body_container--section_nav * {
  touch-action: pan-x;
}

.body-container.body_container--section_nav {
  padding-bottom: 0 !important;
  padding-top: 0 !important;
}

.body-container.body_container--section_nav .viewport {
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}

.body-container.body_container--section_nav {
  padding-bottom: 0 !important;
  padding-top: 2px !important;
  position: sticky;
  bottom: -2px;
  z-index: 25;
  background-color: #FFF;
}

.body-container.body_container--section_nav .viewport {
  padding-bottom: 24px;
  padding-top: 24px;
  overflow: hidden;
  overflow-x: auto;
}

.body-container.body_container--section_nav .body-container-scroller {
  grid-template-columns: repeat(200, min-content) !important;
  white-space: nowrap;
}

.body-container.body_container--section_nav > .viewport::before {
  display: none;
}
</style>